import { bindSession, type Session } from '../api/client';

/**
 * Where the session lives, and why it lives there.
 *
 * `localStorage`, deliberately, and the tradeoff is worth stating rather than assuming. The API
 * returns a Bearer access token and a refresh token in the response BODY — there is no
 * `Set-Cookie`, so an httpOnly cookie is not on the table without a server change. Given that, the
 * choice is between memory (lost on every reload, so an accountant is signed out by refreshing a
 * page) and localStorage (readable by any script that gets onto the origin).
 *
 * localStorage wins here because the exposure it adds is small relative to what an injected script
 * could already do: this app has no third-party scripts, and a script on this origin could simply
 * call the API as the user regardless of where the token sat. If that changes — an embed, an
 * analytics tag — this decision should be revisited, and the server should hand out a cookie.
 *
 * The COMPANY is stored with the session on purpose. It is not a preference: it is half of the
 * identity of every request, and a reload that forgot it would leave the app authenticated and
 * unable to call anything.
 */

const KEY = 'workbook.session';

let current: Session | null = read();
/**
 * DS-11a4 (F53): the company somebody left for the picker, so "Choose a company" can offer the way back. In memory
 * only: a reload, or another session, starts without one, and signing out forgets it.
 */
let previous: string | null = null;
/**
 * DS-11a4 review (F53, WCAG 2.4.3): set by Cancel on "Choose a company", read once by the shell as it mounts, so focus
 * goes back to the company button it left from rather than to <body>.
 */
let cameBack = false;
const listeners = new Set<() => void>();

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (!parsed.accessToken || !parsed.refreshToken) return null;
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      companyId: parsed.companyId ?? null,
      mustChangePassword: parsed.mustChangePassword ?? false,
    };
  } catch {
    // A private window, cleared site data, or a browser refusing storage. Signed out is the right
    // answer to all three, and it must not be a crash on the first line of the app.
    return null;
  }
}

export function setSession(next: Session | null): void {
  current = next;
  if (!next) {
    previous = null;
    cameBack = false;
  }
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    // Nothing to do — the session still works for this tab, it just will not survive a reload.
  }
  for (const listener of listeners) listener();
}

export function getSession(): Session | null {
  return current;
}

export function setMustChangePassword(value: boolean): void {
  if (!current) return;
  setSession({ ...current, mustChangePassword: value });
}

export function setCompany(companyId: string | null): void {
  if (!current) return;
  previous = current.companyId;
  setSession({ ...current, companyId });
}

/** The company left for the picker, or null (DS-11a4). */
export const previousCompanyId = (): string | null => previous;

/** Cancel on "Choose a company": back to the company left, and the shell puts focus back on its company button. */
export function returnToCompany(companyId: string): void {
  cameBack = true;
  setCompany(companyId);
}

/** True once after `returnToCompany` (the shell's mount). */
export function takeCameBack(): boolean {
  const was = cameBack;
  cameBack = false;
  return was;
}

/** For `useSyncExternalStore`, so every component sees one session and re-renders together. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function snapshot(): Session | null {
  return current;
}

// The client refreshes tokens on a 401 and needs to write the rotated pair back. Bound here rather
// than imported the other way, so `client.ts` holds no state and can be used from a test.
bindSession(getSession, setSession);
